/**
 * Shared machinery for linear-equation topic generators.
 *
 * Every equation topic follows the same safe pattern: choose the solution
 * first, assemble an equation true by construction, then validate it
 * independently and finalize into a Question with derived worked steps. This
 * module factors out the finalize/validate/quality/dedup loop so each topic
 * only has to describe how it builds a displayed problem for a chosen solution.
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearEquation } from "../engine/linear";
import {
  validateEquation,
  crossCheckEquationPresentation,
} from "../engine/validate";
import type {
  Difficulty,
  GenerateContext,
  Hint,
  Question,
  QuestionSignature,
  SolutionStep,
} from "./types";
import { getTopic, type TopicId } from "../curriculum/curriculum";
import { deriveSolutionSteps } from "./equation-format";

/** Variables used across equation topics for surface variety. */
export const EQUATION_VARIABLES = ["x", "n", "r", "a", "v", "m", "p"] as const;

/** A built one-solution instance a topic hands back for finalizing. */
export interface BuiltEquation {
  readonly family: string;
  readonly variable: string;
  /** Displayed equation LaTeX (may include parentheses before distributing). */
  readonly displayLatex: string;
  /** Displayed plain text for the reparse cross-check. */
  readonly displayText: string;
  /** Simplified canonical equation for solving/validation. */
  readonly equation: LinearEquation;
  readonly solution: Rational;
  /** Optional leading step to prepend (e.g. distribution). */
  readonly leadingStep?: SolutionStep;
  /** Optional extra hints prepended before the standard isolation hints. */
  readonly leadingHints?: readonly string[];
}

export interface EquationTopicOptions {
  readonly topic: TopicId;
  readonly version: string;
  readonly maxMagnitude: (difficulty: Difficulty) => bigint;
  readonly requireIntegerSolution?: (difficulty: Difficulty) => boolean;
  /** Build a displayed instance for a chosen solution, or null to retry. */
  readonly build: (
    rng: Rng,
    difficulty: Difficulty,
  ) => BuiltEquation | null;
  /** Optional extra quality gate; return false to reject and regenerate. */
  readonly quality?: (b: BuiltEquation) => boolean;
  /**
   * Whether the displayed text is a plain linear equation the reparser can
   * read (default true). Topics whose display is not a linear equation (e.g.
   * proportions shown as a/b = c/d) set this false; their `math` field still
   * carries the validated canonical LinearEquation.
   */
  readonly reparseCrossCheck?: boolean;
}

/**
 * Finalize a built instance into a validated Question, or null if it fails
 * validation/quality. Shared by every equation topic.
 */
export function finalizeEquation(
  opts: EquationTopicOptions,
  ctx: GenerateContext,
  built: BuiltEquation,
): Question | null {
  const spec = {
    expectedCategory: "one" as const,
    expectedValue: built.solution,
    maxAbsMagnitude: opts.maxMagnitude(ctx.difficulty),
    ...(opts.requireIntegerSolution?.(ctx.difficulty)
      ? { requireIntegerSolution: true }
      : {}),
  };
  if (!validateEquation(built.equation, spec).ok) return null;
  if (
    opts.reparseCrossCheck !== false &&
    !crossCheckEquationPresentation(built.equation, built.displayText).ok
  ) {
    return null;
  }
  // Baseline quality: never a zero solution for the current calibration set.
  if (built.solution.isZero()) return null;
  if (opts.quality && !opts.quality(built)) return null;

  const structure = structureKey(built);
  if (
    ctx.recent?.some(
      (r) => r.generator === opts.topic && r.structure === structure,
    )
  ) {
    return null;
  }

  const topic = getTopic(opts.topic);
  const steps: SolutionStep[] = [];
  if (built.leadingStep) steps.push(built.leadingStep);
  steps.push(...deriveSolutionSteps(built.equation, built.variable));

  const hints: Hint[] = [];
  for (const h of built.leadingHints ?? []) {
    hints.push({ level: hints.length + 1, text: h });
  }
  hints.push({
    level: hints.length + 1,
    text: "Use inverse operations to get the variable term by itself.",
  });
  hints.push({
    level: hints.length + 1,
    text: "Divide both sides by the coefficient of the variable.",
  });

  return {
    id: `${opts.topic}-${ctx.seed}-${built.family}`,
    generator: opts.topic,
    generatorVersion: opts.version,
    seed: ctx.seed,
    unit: topic.unit,
    topic: opts.topic,
    family: built.family,
    difficulty: ctx.difficulty,
    prompt: { text: built.displayText, latex: built.displayLatex },
    answerType: "number",
    answer: { kind: "number", value: built.solution },
    steps,
    hints,
    subskills: topic.subskills,
    math: built.equation,
  };
}

export function structureKey(b: BuiltEquation): string {
  return [
    b.family,
    b.equation.left.coeff.key(),
    b.equation.left.constant.key(),
    b.equation.right.coeff.key(),
    b.equation.right.constant.key(),
  ].join("|");
}

export function equationSignature(
  topic: TopicId,
  q: Question,
): QuestionSignature {
  const eq = q.math as LinearEquation | undefined;
  const structure = eq
    ? [
        q.family,
        eq.left.coeff.key(),
        eq.left.constant.key(),
        eq.right.coeff.key(),
        eq.right.constant.key(),
      ].join("|")
    : `${q.family}:${q.answer.kind}`;
  return { generator: topic, family: q.family, difficulty: q.difficulty, structure };
}

/**
 * The regenerate-until-valid loop every equation topic uses.
 */
export function generateEquation(
  opts: EquationTopicOptions,
  ctx: GenerateContext,
): Question {
  const maxAttempts = 200;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const rng = new Rng(`${ctx.seed}#${attempt}`);
    const built = opts.build(rng, ctx.difficulty);
    if (!built) continue;
    const q = finalizeEquation(opts, ctx, built);
    if (q) return q;
  }
  throw new Error(
    `${opts.topic}: failed to generate a valid question after ${maxAttempts} attempts (seed ${ctx.seed}, ${ctx.difficulty})`,
  );
}

/** Convenience: a signed integer coefficient. */
export function coeffInt(
  rng: Rng,
  min: number,
  max: number,
  pNeg: number,
): Rational {
  return rat(rng.int(min, max) * rng.sign(pNeg));
}
